import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Star, ArrowRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API } from '@/lib/api';

const EASE = [0.22, 1, 0.36, 1];

// Fallback data (used if API is unreachable — same as initial pricing)
const FALLBACK = {
  plans: [
    { id: 'f1', name: '1x / Semaine', price: '59', period: '/mois', active: true, features: ['1 cours par semaine', 'Accès aux WOD & HYROX', 'Encadrement professionnel'] },
    { id: 'f2', name: '2x / Semaine', price: '69', period: '/mois', active: true, features: ['2 cours par semaine', 'Accès aux WOD & HYROX', 'Encadrement professionnel'] },
    { id: 'f3', name: '3x / Semaine', price: '89', period: '/mois', popular: true, active: true, features: ['3 cours par semaine', 'Accès aux WOD & HYROX', 'Free Access inclus', 'Encadrement professionnel'] },
    { id: 'f4', name: 'Illimité', price: '99', period: '/mois', active: true, features: ['Cours illimités', 'WOD CrossFit & WOD HYROX', 'Free Access inclus', 'WOD Team CrossFit & HYROX', 'WOD Team XL mensuel', 'Encadrement professionnel'] },
  ],
  carnets: [
    { id: 'c1', name: 'Carnet 10 séances', price: '149€', active: true },
    { id: 'c2', name: 'Carnet 5 séances', price: '79€', active: true },
    { id: 'c3', name: 'Drop-in', price: '18€', active: true },
    { id: 'c4', name: 'Coaching individuel', price: 'Sur devis', active: true },
  ],
  note: "Free Access disponible pour les abonnements 3x/semaine et Illimité.",
};

export const Memberships = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pricing`);
        setData(data);
      } catch {
        setData(FALLBACK);
      }
    })();
  }, []);

  if (!data) {
    return (
      <section id="pricing" className="section-padding flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </section>
    );
  }

  const plans = (data.plans || []).filter((p) => p.active).sort((a, b) => (a.order || 0) - (b.order || 0));
  const carnets = (data.carnets || []).filter((c) => c.active).sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="section-padding relative bg-background overflow-hidden"
    >
      <div className="absolute -top-40 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-primary-glow/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-center mb-16"
        >
          <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">
            ━━ Tarifs
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl text-dark mt-4 mb-4">
            ABONNEMENTS &{' '}
            <span className="text-gradient-primary italic font-display inline-block pr-2">
              CARNETS
            </span>
          </h2>
          <p className="text-muted-foreground text-base max-w-2xl mx-auto">
            Sans engagement, préavis de 2 mois. Frais d'adhésion : 99€.
          </p>
        </motion.div>

        {/* Plans */}
        <div className={`grid sm:grid-cols-2 ${plans.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-5 max-w-6xl mx-auto mb-14 pt-4`}>
          {plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-5%' }}
              transition={{ duration: 0.7, delay: idx * 0.1, ease: EASE }}
              whileHover={{ y: -8 }}
            >
              <Card
                data-testid={`pricing-card-${idx}`}
                className={`relative p-7 h-full transition-all duration-500 border-2 ${
                  plan.popular
                    ? 'border-primary bg-gradient-to-br from-white via-white to-primary/5 shadow-[0_20px_60px_-15px_hsla(155,75%,18%,0.25)] pt-9'
                    : 'border-border hover:border-primary/40 hover:shadow-[0_15px_50px_-12px_hsla(155,75%,18%,0.15)]'
                } rounded-2xl`}
              >
                {plan.popular && (
                  <>
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-glow via-primary to-primary-dark rounded-t-2xl" />
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1.5 rounded-full text-[10px] font-bold tracking-widest flex items-center gap-1 whitespace-nowrap shadow-lg z-10">
                      <Star className="h-3 w-3 fill-white" /> POPULAIRE
                    </div>
                  </>
                )}

                <div className="mb-6">
                  <h3 className="font-heading text-lg text-dark mb-3 tracking-wide">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className={`font-display text-5xl leading-none ${plan.popular ? 'text-primary' : 'text-dark'}`}>{plan.price}</span>
                    <span className="font-display text-2xl text-dark">€</span>
                    <span className="text-muted-foreground text-sm font-medium ml-1">{plan.period}</span>
                  </div>
                </div>

                <div className="space-y-3 mb-7">
                  {(plan.features || []).map((feature, i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${plan.popular ? 'bg-primary' : 'bg-primary/10'}`}>
                        <Check className={`h-3 w-3 ${plan.popular ? 'text-white' : 'text-primary'}`} strokeWidth={3} />
                      </div>
                      <span className="text-sm text-foreground leading-tight">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link to="/contact">
                  <Button
                    className={`btn-shine w-full font-heading tracking-wider text-sm py-6 rounded-xl transition-all duration-300 group ${
                      plan.popular ? 'bg-primary hover:bg-primary-light text-white glow-primary' : 'bg-dark hover:bg-primary text-white'
                    }`}
                  >
                    CHOISIR
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Carnets */}
        {carnets.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-10%' }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="max-w-4xl mx-auto"
          >
            <div className={`grid grid-cols-2 ${carnets.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
              {carnets.map((carnet, idx) => (
                <div
                  key={carnet.id}
                  data-testid={`carnet-${idx}`}
                  className="bg-white rounded-2xl p-5 text-center border border-border hover:border-primary/40 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                >
                  <h4 className="font-heading text-sm text-dark mb-2 tracking-wide">{carnet.name}</h4>
                  <p className="font-display text-3xl text-primary leading-none">{carnet.price}</p>
                </div>
              ))}
            </div>
            {data.note && <p className="text-center text-muted-foreground text-xs mt-6 tracking-wide">{data.note}</p>}
          </motion.div>
        )}

        {/* CTA — Souscrire en ligne via Deciplus */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-10%' }}
          transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
          className="mt-14 max-w-3xl mx-auto"
        >
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-primary to-primary-dark p-8 md:p-10 text-center">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-primary-glow/20 rounded-full blur-[80px] pointer-events-none" />
            <div className="relative z-10">
              <span className="inline-block text-primary-glow font-semibold text-[10px] tracking-[0.3em] uppercase mb-3">
                ━━ Nouveau
              </span>
              <h3 className="font-heading text-2xl md:text-3xl text-white mb-3 tracking-tight">
                Souscris ton abonnement{' '}
                <span className="italic font-display">en ligne</span>
              </h3>
              <p className="text-white/75 text-sm md:text-base leading-relaxed mb-6 max-w-xl mx-auto">
                Paiement sécurisé, activation immédiate, facture par email. Aucun rendez-vous nécessaire.
              </p>
              <Link to="/abonnements-en-ligne" data-testid="memberships-cta-boutique">
                <Button
                  size="lg"
                  className="btn-shine bg-white hover:bg-white/90 text-primary font-heading tracking-wider text-sm px-8 py-6 rounded-xl group"
                >
                  SOUSCRIRE EN LIGNE
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
